import React, { useState, useEffect } from "react";
import {
    Card,
    Button,
    Tag,
    Space,
    Typography,
    Row,
    Col,
    List,
    Spin,
    Alert,
    Empty,
} from "antd";
import {
    CheckCircleOutlined,
    StarOutlined,
    ThunderboltOutlined,
    SafetyOutlined,
    CrownOutlined,
    RocketOutlined,
} from "@ant-design/icons";
import api from "utils/api";
import { useAuth } from "context/AuthContext";
import { useNavigate } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;

const renderFeatures = (featuresArray, isPremiumCard = false) => {
    if (!featuresArray || featuresArray.length === 0) return null;
    const validFeatures = featuresArray.filter(f => typeof f === "string");
    if (validFeatures.length === 0) return null;
    return (
        <List
            size="small"
            dataSource={validFeatures}
            renderItem={(feature) => (
                <List.Item style={{ padding: "6px 0", border: 'none' }}>
                    <Space size="middle">
                        <CheckCircleOutlined style={{ color: isPremiumCard ? "#ffffff" : "#10B981", fontSize: "14px" }} />
                        <Text style={{ color: isPremiumCard ? "#FFFFFF" : "#4B5563", fontSize: "14px", fontWeight: 500 }}>
                            {feature}
                        </Text>
                    </Space>
                </List.Item>
            )}
        />
    );
};

const PlanCard = ({ plan, currentUserPlan }) => {
    const navigate = useNavigate();
    const isPremium = plan.name.toLowerCase().includes("premium") || plan.price > 400;

    const getPlanIcon = () => {
        if (isPremium) return <CrownOutlined style={{ fontSize: "28px", color: "#F59E0B" }} />;
        if (plan.name.toLowerCase().includes("standard")) return <RocketOutlined style={{ fontSize: "28px", color: "#8B5CF6" }} />;
        return <SafetyOutlined style={{ fontSize: "28px", color: "#3B82F6" }} />;
    };

    const getPlanColor = () => {
        if (isPremium) return "#7C3AED";
        if (plan.name.toLowerCase().includes("standard")) return "#8B5CF6";
        return "#3B82F6";
    };

    const isCurrentPlan = currentUserPlan && currentUserPlan._id === plan._id;
    const canUpgrade = currentUserPlan && currentUserPlan.price < plan.price;
    const isDowngrade = currentUserPlan && currentUserPlan.price > plan.price;

    let buttonText = "Choose Plan";
    let buttonDisabled = false;
    let buttonAction = () => {
        sessionStorage.setItem('selectedPlanId', plan._id);
        navigate(`/signup/${plan.name.replace(/\s+/g, '-').toLowerCase()}`);
    };
    let buttonStyle = {
        background: isPremium ? "#FFFFFF" : getPlanColor(),
        borderColor: isPremium ? "#FFFFFF" : getPlanColor(),
        color: isPremium ? getPlanColor() : "#FFFFFF",
    };

    if (currentUserPlan) {
        if (isCurrentPlan) {
            buttonText = "Current Plan";
            buttonDisabled = true;
            buttonStyle = { background: "#F3F4F6", borderColor: "#D1D5DB", color: "#4B5563" };
        } else if (canUpgrade) {
            buttonText = "Upgrade";
            buttonAction = () => {
                sessionStorage.setItem('selectedPlanId', plan._id);
                sessionStorage.setItem('upgradePlanData', JSON.stringify({
                    planId: plan._id,
                    planName: plan.name,
                    planPrice: plan.price,
                    isUpgrade: true
                }));
                navigate(`/upgrade-checkout/${plan._id}`);
            };
        } else if (isDowngrade) {
            buttonText = "Cannot Downgrade";
            buttonDisabled = true;
            buttonStyle = { background: "#F3F4F6", borderColor: "#D1D5DB", color: "#9CA3AF" };
        }
    }

    return (
        <Col xs={24} sm={12} lg={8}>
            <Card
                className={`h-full transform transition-all duration-300 hover:scale-105 hover:shadow-2xl ${isPremium ? "ring-2 ring-purple-500" : ""}`}
                style={{
                    background: isPremium ? "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)" : "#FFFFFF",
                    borderRadius: "16px",
                    boxShadow: isPremium ? "0 20px 40px -15px rgba(99, 102, 241, 0.5)" : "0 10px 20px -10px rgba(0, 0, 0, 0.1)"
                }}
                bodyStyle={{ padding: "32px", height: "100%", display: "flex", flexDirection: "column" }}
            >
                {isPremium && <div className="text-center mb-6"><Tag icon={<StarOutlined />} color="#F59E0B" style={{ borderRadius: '9999px', padding: '4px 12px', fontWeight: '600' }}>Most Popular</Tag></div>}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">{getPlanIcon()}</div>
                    <Title level={2} style={{ color: isPremium ? "#FFFFFF" : "#1F2937", fontSize: "24px", fontWeight: '700' }}>{plan.name}</Title>
                    <Paragraph style={{ color: isPremium ? "rgba(255, 255, 255, 0.9)" : "#6B7280", fontSize: "16px" }}>
                        {plan.description || "Perfect plan for your business"}
                    </Paragraph>
                </div>
                <div className="text-center mb-10">
                    <Text style={{ fontSize: "48px", fontWeight: 800, color: isPremium ? "#FFFFFF" : "#1F2937" }}>${plan.price}</Text>
                    <Text style={{ fontSize: "16px", color: isPremium ? "rgba(255, 255, 255, 0.8)" : "#6B7280" }}>/{plan.interval || "month"}</Text>
                </div>
                <div className="flex-grow mb-10">
                    {plan.features?.map((group, index) => (
                        <div key={index} className="mb-8">
                            <div className="flex items-center mb-4">
                                <ThunderboltOutlined style={{ color: isPremium ? "#FBBF24" : getPlanColor(), marginRight: "12px", fontSize: "20px" }} />
                                <Title level={4} style={{ color: isPremium ? "#FFFFFF" : "#374151", margin: 0, fontSize: "18px", fontWeight: '600' }}>{group.title || "Features"}</Title>
                            </div>
                            {renderFeatures(group.features, isPremium)}
                        </div>
                    ))}
                </div>
                <Button
                    type="primary"
                    size="large"
                    block
                    onClick={buttonAction}
                    disabled={buttonDisabled}
                    style={{
                        ...buttonStyle,
                        height: "48px",
                        fontSize: "16px",
                        fontWeight: 600,
                        borderRadius: "12px",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    {buttonText}
                </Button>
            </Card>
        </Col>
    );
};

const UpgradePricingPage = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const [currentUserPlan, setCurrentUserPlan] = useState(null);

    useEffect(() => {
        const fetchPlansAndUserData = async () => {
            try {
                setLoading(true);
                const plansResponse = await api.get("/plan/");
                if (!plansResponse.data?.success) throw new Error("Failed to fetch plans.");

                const activePlans = plansResponse.data.content.filter(p => p.isActive).sort((a, b) => a.price - b.price);
                setPlans(activePlans);

                if (user && user._id) {
                    const businessResponse = await api.get(`/business/by_user/${user._id}`);
                    const businesses = businessResponse.data?.content;
                    if (businessResponse.data?.success && businesses?.length > 0) {
                        const userPlanId = businesses[0].plan_id;
                        if (userPlanId) {
                            const fullPlanObject = activePlans.find(p => p._id === userPlanId);
                            setCurrentUserPlan(fullPlanObject || null);
                        }
                    }
                }
            } catch (err) {
                if (err.response?.status !== 404) {
                    setError(err.message || "An error occurred.");
                }
            } finally {
                setLoading(false);
            }
        };
        fetchPlansAndUserData();
    }, [user]);

    if (loading) return <div className="flex justify-center items-center min-h-screen bg-gray-50"><Spin size="large" /></div>;
    if (error) return <div className="flex justify-center items-center min-h-screen bg-gray-50"><Alert message="Error" description={error} type="error" showIcon /></div>;
    if (plans.length === 0) return <div className="flex justify-center items-center min-h-screen bg-gray-50"><Empty /></div>;

    return (
        <div className="min-h-screen w-full bg-gray-50 py-16 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <Title level={1} style={{ fontSize: '42px', fontWeight: '800' }}>Find the Perfect Plan for You</Title>
                    <Paragraph style={{ fontSize: '18px', color: '#6B7280', maxWidth: '600px', margin: '0 auto' }}>
                        Choose a plan that suits your needs and unlock powerful features to boost your business.
                    </Paragraph>
                </div>
                <Row gutter={[32, 32]} justify="center">
                    {plans.map((plan) => (
                        <PlanCard
                            key={plan._id}
                            plan={plan}
                            currentUserPlan={currentUserPlan}
                        />
                    ))}
                </Row>
            </div>
        </div>
    );
};

export default UpgradePricingPage;
